import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Typography, Button, CircularProgress } from '@mic3/platform-ui';
import PageTemplate from 'app/components/templates/PageTemplate';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import styled from 'styled-components';
import { loadMapSystemSettings, setDocumentTitle, updateMapSystemSettings } from 'store/actions/app/appActions';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'app/components/atoms/Loader/Loader';
import { getPermissions } from 'app/config/rolesConfig';
import { Link } from 'react-router-dom';
import { baseRoutes } from 'app/config/routesConfig';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import { generateAvailableLayers } from 'app/utils/maps/layer/layerUtils';

const StyledTypography = styled(Typography)`
    color: ${({ theme }) => theme.material.colors.text.primary};
`;

const NavigationLink = styled(Link)`
    color: ${({ theme }) => theme.material.colors.primary.main};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;

    &:hover {
        text-decoration: underline;
    }
`;

const InfoText = styled.div`
    color: ${({ theme }) => theme.material.colors.text.secondary};
    font-size: 14px;
`;

const MapsModuleAdminSection = () => {
    const mapSettings = useSelector((state) => state.app.mapSettings);
    const mapSettingsLoading = useSelector((state) => state.app.mapSettingsLoading);
    const [formState, setFormState] = useState({});
    const [formTouched, setFormTouched] = useState({ changed: false });
    const [formRevertKey, setFormRevertKey] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef(null);
    const dispatch = useDispatch();

    useEffect(() => {
        if (mapSettingsLoading) return;
        setFormState({ ...(mapSettings || {}) });
    }, [mapSettings, mapSettingsLoading]);


    useEffect(() => {
        dispatch(loadMapSystemSettings());
        dispatch(setDocumentTitle('Admin Console - Manage Situational Awareness'));
    }, [dispatch]);

    const mapsModuleLayerOptions = useMemo(() => {
        return generateAvailableLayers({ ...(formState ||{}), enableGoogleMaps: false }); // As we don't give option of any google layer on maps module
    }, [formState]);

    const formDefinitions = useMemo(() => {
        return [
            {
                type: 'panel',
                properties: {
                    header: 'Base Layers',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'typeahead',
                        properties: {
                            name: 'defaultMapBaseLayer',
                            label: 'Default base layer for maps module',
                            options: mapsModuleLayerOptions,
                            defaultValue: 'OSM',
                        },
                    },
                    {
                        type: 'custom',
                        properties: {
                            cid: 'Google Maps Info',
                            noBackground: true,
                            Component: () => {
                                return <InfoText>Google map layers are not included because of licensing restrictions.</InfoText>;
                            },
                        },
                    },
                ],
            },
            {
                type: 'panel',
                properties: {
                    header: 'Site Map Settings',
                    collapsible: false,
                },
                children: [
                    {
                        type: 'custom',
                        properties: {
                            cid: 'Site Map Settings Link',
                            noBackground: true,
                            Component: () => {
                                return (
                                    <InfoText>
                                        Change general map settings <NavigationLink to={baseRoutes['site-maps']}>here</NavigationLink>.
                                    </InfoText>
                                );
                            },
                        },
                    },
                ],
            },
        ];
    }, [mapsModuleLayerOptions]);

    const { canEdit } = getPermissions(mapSettings?.role) || {};

    const revertChanges = () => {
        setFormState({ ...(mapSettings || {}) });
        setFormTouched({ changed: false });
        setFormRevertKey(prev => prev + 1);
    };

    const onFormChange = (data) => {
        const formData = { ...(data || {}) };
        setFormState(formData);
        setFormTouched({ changed: true });
    };

    const saveSettings = async () => {
        setIsSaving(true);
        try {
            const availableLayerValues = mapsModuleLayerOptions.map(layer => layer.value);
            const validatedFormState = { ...formState };
            if (formState.defaultMapBaseLayer && !availableLayerValues.includes(formState.defaultMapBaseLayer)) {
                validatedFormState.defaultMapBaseLayer = null;
            }
            await dispatch(updateMapSystemSettings(validatedFormState));
            dispatch(loadMapSystemSettings());
        } catch (error) {}
        setIsSaving(false);
    };
    const renderSaveButton = () => {
        return (
            <>
                {canEdit ? (
                    <>
                        <Button 
                            onClick={saveSettings} 
                            color='primary' 
                            form='form' 
                            type='submit' 
                            style={{ marginRight: '10px' }}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} color="primary" /> : 'Save'}
                        </Button>
                        <Button disabled={!formTouched.changed} variant='outlined' onClick={revertChanges} color='primary' form='form'>
                            Revert
                        </Button>{' '}
                    </>
                ) : null}
            </>
        );
    };

    return (
        <PageTemplate title='Situational_Awareness'>
            <HeaderBar left={<StyledTypography variant='h6'>Manage Situational Awareness</StyledTypography>} />
            <ContentArea>
                <HeaderBar right={renderSaveButton()} rowDirection />
                {mapSettingsLoading && <Loader absolute />}
                <FormGenerator
                    disabled={!canEdit}
                    key={formRevertKey}
                    onChange={onFormChange}
                    ref={formRef}
                    data={formState}
                    components={formDefinitions}
                />
            </ContentArea>
        </PageTemplate>
    );
};

export default MapsModuleAdminSection;
